import type { MonitoringInvestigationContext } from "../../../shared/chatInvestigation";
import { AskMonitoringChat } from "./AskMonitoringChat";
import { styled } from "goober";
import { useCallback, useMemo, useState } from "react";
import type {
  ConnectorReportRow,
  MonitoringConnectorUsageResponse,
  SourceReportRow,
  WorkflowReportRow,
} from "../../../shared/connectorUsageReport";
import { queries } from "../../../shared/queries";
import type { SpaceMode } from "../../../shared/schema";
import { useQuery } from "@rocicorp/zero/react";
import { useRevisionedJson } from "../../hooks/useRevisionedJson";
import { useSpaceDisplayName } from "../../hooks/useSpaceDisplayName";
import { monitoringConnectorUsagePath } from "../../lib/apiPaths";
import { navigate, routes, useCurrentUrl } from "../../lib/urls";
import {
  SearchableDropdown,
  MIN_OPTIONS_FOR_SEARCH,
  RowButton,
  SectionLabel,
} from "../SearchableDropdown";
import { ConnectorLogo } from "../ConnectorLogo";
import { createdByName } from "../ConnectorRow";
import { Icon } from "../Icon";
import { Skeleton } from "../Skeleton";
import { Text } from "../Text";
import { useMonitoringInteraction } from "./analytics";
import {
  ConnectorRequestVolumePanel,
  type OverviewStat,
  type RequestVolumeSeries,
} from "./ConnectorRequestVolumePanel";
import { ConnectorBreakdownPanel } from "./ConnectorBreakdownPanel";
import {
  ConnectorVolumeBars,
  type ConnectorVolumeBar,
} from "./ConnectorVolumeBars";
import {
  computeConnectorSummaries,
  computeDailyVolume,
  computeTrafficOverview,
  computeWorkflowSummaries,
  connectorUsageSpaceIds,
  filterUsageRows,
  formatErrorRatePercent,
  formatRequestCount,
  NO_SPACE,
} from "./connectorTraffic";
import { ControlButton } from "./ControlButton";
import { MonitoringLayout } from "./MonitoringLayout";
import { MonitoringScopeSelector } from "./MonitoringScopeSelector";
import { MonitoringSpacePicker, PickerRowCheck } from "./MonitoringSpacePicker";
import { dayFloor, windowOption, windowRange } from "./monitoringWindow";
import { MonitoringWindowPicker } from "./MonitoringWindowPicker";
import { SPACE_COLORS } from "./monitoringPanelLayout";
import { foldsPersonal, PERSONAL_BUCKET } from "./monitoringScope";
import { useMonitoringScope } from "./useMonitoringScope";
import { RefreshButton } from "./RefreshButton";
import { ConnectorsGrid, Content } from "./workflowsLayout";

const OTHER_CONNECTOR = "__other__";

const EMPTY_DAILY: readonly ConnectorReportRow[] = [];
const EMPTY_WORKFLOWS: readonly WorkflowReportRow[] = [];
const EMPTY_SOURCES: readonly SourceReportRow[] = [];

// Gray is the palette's reserved "other/none", so it carries both the folded
// connector tail and usage with no space.
const UNATTRIBUTED_COLOR = "var(--gray-600)";

const PickerTrigger = styled(ControlButton)`
  max-width: 280px;
`;

function LoadingPlaceholder() {
  return (
    <Content aria-hidden>
      <ConnectorsGrid>
        <Skeleton
          style={{
            width: "100%",
            height: "100%",
            minHeight: 220,
            borderRadius: 12,
          }}
        />
        <Skeleton
          style={{
            width: "100%",
            height: "100%",
            minHeight: 220,
            borderRadius: 12,
          }}
        />
        <Skeleton
          style={{
            width: "100%",
            height: "100%",
            minHeight: 220,
            borderRadius: 12,
          }}
        />
      </ConnectorsGrid>
    </Content>
  );
}

export function ConnectorsPanel() {
  const recordInteraction = useMonitoringInteraction();
  const displaySpaceName = useSpaceDisplayName();
  const {
    window: windowParam = "this_week",
    space: spaceParam,
    connector: connectorParam,
  } = useCurrentUrl(routes.monitoring.connectors);
  const selectedWindow = windowOption(windowParam);
  const [today0] = useState(() => dayFloor(Date.now()));
  const { start, end } = windowRange(selectedWindow.value, today0);

  const [refreshToken, setRefreshToken] = useState(0);
  // Read already collapsed rather than synced row by row, so the window only
  // moves on a refresh.
  const {
    data: usage,
    loading: usageLoading,
    refreshing: usageRefreshing,
  } = useRevisionedJson<MonitoringConnectorUsageResponse>(
    monitoringConnectorUsagePath(),
    `start=${start}&end=${end}`,
    refreshToken
  );
  // Named for the AI tab but shared by both monitoring tabs so they hydrate the
  // connector list once.
  const [connectors, connectorsQuery] = useQuery(queries.monitorAiConnectors());
  const [workflows, workflowsQuery] = useQuery(queries.allWorkflows());
  const [spaces, spacesQuery] = useQuery(
    queries.spacesLite({ includeAdminSpaces: true })
  );

  const isLoading =
    usageLoading ||
    connectorsQuery.type !== "complete" ||
    workflowsQuery.type !== "complete" ||
    spacesQuery.type !== "complete";

  // Held so a window change doesn't blank the panels mid-fetch. A failed fetch
  // resolves with no payload, and clearing on that reads as a tenant with no
  // traffic at all.
  const [heldUsage, setHeldUsage] = useState(usage);
  if (!isLoading && usage && usage !== heldUsage) setHeldUsage(usage);
  const allTrafficRows = heldUsage?.daily ?? EMPTY_DAILY;
  const allWorkflowRows = heldUsage?.workflows ?? EMPTY_WORKFLOWS;
  const allSourceRows = heldUsage?.sources ?? EMPTY_SOURCES;

  const { scope, personalIds, matchesSpace } = useMonitoringScope(
    routes.monitoring.connectors
  );
  const scopedTrafficRows = useMemo(
    () => allTrafficRows.filter((row) => matchesSpace(row.spaceId)),
    [allTrafficRows, matchesSpace]
  );
  const scopedWorkflowRows = useMemo(
    () => allWorkflowRows.filter((row) => matchesSpace(row.spaceId)),
    [allWorkflowRows, matchesSpace]
  );

  const connectorMeta = useMemo(() => {
    const map = new Map<
      string,
      { name: string; identifier: string; creatorName: string | null }
    >();
    for (const connector of connectors ?? []) {
      map.set(connector.id, {
        name: connector.name,
        identifier: connector.identifier,
        creatorName: createdByName(connector.createdBy),
      });
    }
    return map;
  }, [connectors]);

  const allSummaries = useMemo(
    () => computeConnectorSummaries(scopedTrafficRows),
    [scopedTrafficRows]
  );

  const spaceOptions = useMemo(() => {
    const { spaceIds, hasUnattributed } =
      connectorUsageSpaceIds(scopedTrafficRows);
    const present = new Set(spaceIds);
    let hasPersonal = false;
    const options: { id: string; label: string; mode: SpaceMode }[] = [];
    for (const space of spaces ?? []) {
      if (!present.has(space.id) || !matchesSpace(space.id)) continue;
      if (foldsPersonal(scope) && personalIds.has(space.id)) {
        hasPersonal = true;
        continue;
      }
      options.push({
        id: space.id,
        label: displaySpaceName(space),
        mode: space.mode,
      });
    }
    options.sort((a, b) => a.label.localeCompare(b.label));
    if (hasPersonal)
      options.push({
        id: PERSONAL_BUCKET,
        label: "Personal",
        mode: "personal",
      });
    return { options, hasUnattributed };
  }, [
    scopedTrafficRows,
    spaces,
    scope,
    personalIds,
    matchesSpace,
    displaySpaceName,
  ]);

  const spaceValid =
    spaceParam != null &&
    (spaceParam === NO_SPACE
      ? spaceOptions.hasUnattributed
      : spaceOptions.options.some((option) => option.id === spaceParam));
  const effectiveSpaceId = spaceValid ? spaceParam : null;

  const connectorValid =
    connectorParam != null &&
    allSummaries.some((summary) => summary.connectorId === connectorParam);
  const effectiveConnectorId = connectorValid ? connectorParam : null;

  // The synthetic "Personal" bucket (shown only under "both") matches any
  // personal space; every other selection is an exact space id or null.
  const filterForSpace = useCallback(
    <T extends { spaceId: string | null; connectorId: string }>(
      rows: readonly T[],
      connectorId: string | null
    ): readonly T[] =>
      effectiveSpaceId === PERSONAL_BUCKET
        ? filterUsageRows(
            rows.filter((r) => r.spaceId != null && personalIds.has(r.spaceId)),
            null,
            connectorId
          )
        : filterUsageRows(rows, effectiveSpaceId, connectorId),
    [effectiveSpaceId, personalIds]
  );

  const filteredTraffic = useMemo(
    () => filterForSpace(scopedTrafficRows, effectiveConnectorId),
    [filterForSpace, scopedTrafficRows, effectiveConnectorId]
  );
  const summaries = useMemo(
    () => computeConnectorSummaries(filteredTraffic),
    [filteredTraffic]
  );

  const workflowSummaries = useMemo(
    () =>
      computeWorkflowSummaries(
        filterForSpace(scopedWorkflowRows, effectiveConnectorId)
      ),
    [filterForSpace, scopedWorkflowRows, effectiveConnectorId]
  );
  // Deleted workflows are left out so the table falls back to "Deleted
  // workflow" and drops the link.
  const workflowsById = useMemo(() => {
    const map = new Map<
      string,
      {
        id: string;
        name: string;
        badgeSeed: string | null;
        pausedAt: number | null;
      }
    >();
    for (const workflow of workflows ?? []) {
      if (!workflow.deletedAt)
        map.set(workflow.id, {
          id: workflow.id,
          name: workflow.name,
          badgeSeed: workflow.badgeSeed,
          pausedAt: workflow.pausedAt,
        });
    }
    return map;
  }, [workflows]);
  const overview = useMemo(
    () => computeTrafficOverview(summaries),
    [summaries]
  );
  // Fill the whole window (end can be in the future for this week/month), so the
  // chart draws the dashed projection past today.
  const volume = useMemo(
    () => computeDailyVolume(filteredTraffic, start, end),
    [filteredTraffic, start, end]
  );

  // Non-workflow usage broken out by source, shown below the workflows in the
  // drill-in. Non-workflow usage belongs to no space, so a space filter excludes
  // it — matching how the connector/workflow totals reconcile.
  const unattributedSources = useMemo(() => {
    if (
      effectiveConnectorId == null ||
      effectiveSpaceId != null ||
      scope !== "both"
    )
      return [];
    return allSourceRows
      .filter((row) => row.connectorId === effectiveConnectorId)
      .map((row) => ({
        source: row.source,
        requests: row.requestCount,
        errors: row.errorCount,
      }))
      .filter((entry) => entry.requests > 0)
      .sort(
        (a, b) => b.requests - a.requests || a.source.localeCompare(b.source)
      );
  }, [allSourceRows, effectiveConnectorId, effectiveSpaceId, scope]);

  const connectorName = useCallback(
    (connectorId: string) =>
      connectorMeta.get(connectorId)?.name ?? "Deleted connector",
    [connectorMeta]
  );

  // The bar chart keeps every connector in the space scope visible (a connector
  // filter fades the unselected rather than dropping them), so it is scoped by
  // space but never by the selected connector.
  const spaceSummaries = useMemo(
    () => computeConnectorSummaries(filterForSpace(scopedTrafficRows, null)),
    [filterForSpace, scopedTrafficRows]
  );

  // One stable color per connector, ranked across the space-scoped set, so a
  // connector reads the same in the bars and the line-chart tooltip.
  const connectorColorById = useMemo(() => {
    const map = new Map<string, string>();
    spaceSummaries.forEach((summary, i) =>
      map.set(summary.connectorId, SPACE_COLORS[i % SPACE_COLORS.length]!)
    );
    return map;
  }, [spaceSummaries]);

  const volumeSeries = useMemo(() => {
    const coloredCount =
      summaries.length > SPACE_COLORS.length
        ? SPACE_COLORS.length - 1
        : summaries.length;
    const series: RequestVolumeSeries[] = [];
    const seriesKeyOf = new Map<string, string>();
    summaries.forEach((summary, i) => {
      if (i < coloredCount) {
        seriesKeyOf.set(summary.connectorId, summary.connectorId);
        series.push({
          key: summary.connectorId,
          label: connectorName(summary.connectorId),
          color: connectorColorById.get(summary.connectorId)!,
        });
      } else {
        seriesKeyOf.set(summary.connectorId, OTHER_CONNECTOR);
      }
    });
    const otherCount = summaries.length - coloredCount;
    if (otherCount > 0) {
      series.push({
        key: OTHER_CONNECTOR,
        label: `${otherCount} other connector${otherCount === 1 ? "" : "s"}`,
        color: UNATTRIBUTED_COLOR,
        isOther: true,
      });
    }
    return { series, seriesKeyOf };
  }, [summaries, connectorName, connectorColorById]);

  // Every connector in the space scope gets a bar (scrolling horizontally once
  // they overflow); a connector filter fades the unselected rather than
  // dropping them.
  const volumeBars = useMemo<ConnectorVolumeBar[]>(
    () =>
      spaceSummaries.map((summary) => ({
        key: summary.connectorId,
        connectorId: summary.connectorId,
        identifier:
          connectorMeta.get(summary.connectorId)?.identifier ?? "unknown",
        label: connectorName(summary.connectorId),
        requests: summary.requests,
        color: connectorColorById.get(summary.connectorId)!,
      })),
    [spaceSummaries, connectorMeta, connectorName, connectorColorById]
  );

  const setParam = (
    key: "window" | "space" | "connector",
    value: string | null,
    interaction:
      | "connectors.window_change"
      | "connectors.space_filter_change"
      | "connectors.connector_filter_change"
  ) => {
    recordInteraction(interaction, { value: value ?? undefined });
    navigate(
      routes.monitoring.connectors,
      { [key]: value ?? "" },
      { replace: true }
    );
  };

  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  if (!isLoading && !hasLoadedOnce) setHasLoadedOnce(true);

  if (isLoading && !hasLoadedOnce) {
    return (
      <MonitoringLayout
        ready={false}
        headerItem={
          <MonitoringScopeSelector route={routes.monitoring.connectors} />
        }
      >
        <LoadingPlaceholder />
      </MonitoringLayout>
    );
  }

  // No usage in the selected window renders a realistic, non-animated skeleton
  // of the populated tab (ghost line, bars, and rows) rather than a bare empty
  // card, matching the other monitoring tabs. The window picker stays live so a
  // different range can still surface data.
  const isEmpty = allSummaries.length === 0;

  const connectorPicker = (
    <SearchableDropdown
      searchable={allSummaries.length >= MIN_OPTIONS_FOR_SEARCH}
      searchPlaceholder="Search connectors…"
      opener={
        <PickerTrigger as="span">
          {effectiveConnectorId ? (
            <ConnectorLogo
              template={{
                name: connectorName(effectiveConnectorId),
                identifier:
                  connectorMeta.get(effectiveConnectorId)?.identifier ??
                  "unknown",
              }}
              size={12}
            />
          ) : (
            <Icon name="layers" size={12} />
          )}
          <Text variant="text-small-md" ellipsis>
            {effectiveConnectorId
              ? connectorName(effectiveConnectorId)
              : "All connectors"}
          </Text>
          <Icon name="chevron-down" size={12} color="var(--text-secondary)" />
        </PickerTrigger>
      }
    >
      {(query, close) => {
        const q = query.trim().toLowerCase();
        const matches = allSummaries.filter((summary) => {
          if (!q) return true;
          const meta = connectorMeta.get(summary.connectorId);
          return `${connectorName(summary.connectorId)} ${meta?.creatorName ?? ""}`
            .toLowerCase()
            .includes(q);
        });
        return (
          <>
            {!q && (
              <RowButton
                onClick={() => {
                  setParam(
                    "connector",
                    null,
                    "connectors.connector_filter_change"
                  );
                  close();
                }}
              >
                <Icon name="layers" size={16} color="var(--text-primary)" />
                <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                  <Text variant="text-regular" ellipsis>
                    All connectors
                  </Text>
                  <Text
                    variant="text-small"
                    color="var(--text-secondary)"
                    ellipsis
                  >
                    {allSummaries.length}{" "}
                    {allSummaries.length === 1 ? "connector" : "connectors"}
                  </Text>
                </div>
                {effectiveConnectorId == null && PickerRowCheck}
              </RowButton>
            )}
            {matches.length === 0 && q !== "" ? (
              <SectionLabel>
                <Text variant="text-small" color="var(--text-secondary)">
                  No connectors found.
                </Text>
              </SectionLabel>
            ) : (
              matches.map((summary) => {
                const name = connectorName(summary.connectorId);
                const meta = connectorMeta.get(summary.connectorId);
                const creator = meta?.creatorName ?? null;
                return (
                  <RowButton
                    key={summary.connectorId}
                    onClick={() => {
                      setParam(
                        "connector",
                        summary.connectorId,
                        "connectors.connector_filter_change"
                      );
                      close();
                    }}
                  >
                    <ConnectorLogo
                      template={{
                        name,
                        identifier: meta?.identifier ?? "unknown",
                      }}
                      size={16}
                    />
                    <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                      <Text variant="text-regular" ellipsis>
                        {name}
                      </Text>
                      {creator && (
                        <Text
                          variant="text-small"
                          color="var(--text-secondary)"
                          ellipsis
                        >
                          {creator}
                        </Text>
                      )}
                    </div>
                    {summary.connectorId === effectiveConnectorId &&
                      PickerRowCheck}
                  </RowButton>
                );
              })
            )}
          </>
        );
      }}
    </SearchableDropdown>
  );

  const spacePicker = (
    <MonitoringSpacePicker
      options={spaceOptions.options}
      hasUnattributed={spaceOptions.hasUnattributed}
      value={effectiveSpaceId}
      onChange={(id) => setParam("space", id, "connectors.space_filter_change")}
    />
  );

  const windowPicker = (
    <MonitoringWindowPicker
      value={selectedWindow}
      onChange={(option) =>
        setParam("window", option.value, "connectors.window_change")
      }
    />
  );

  const stats: OverviewStat[] = [
    { label: "Requests", value: overview.requests, format: formatRequestCount },
    { label: "Errors", value: overview.errors, format: formatRequestCount },
    {
      label: "Error rate",
      value:
        overview.requests === 0
          ? 0
          : (overview.errors / overview.requests) * 100,
      format: overview.requests === 0 ? () => "—" : formatErrorRatePercent,
    },
  ];

  const refreshButton = (
    <RefreshButton
      spinning={usageRefreshing}
      onClick={() => setRefreshToken((token) => token + 1)}
    />
  );

  const investigation: MonitoringInvestigationContext = {
    period: selectedWindow.label,
    timeRange: { start, end: end + 86_400_000 },
    filters: { spaceId: effectiveSpaceId, connectorId: effectiveConnectorId },
  };
  const trafficSnapshot = `${overview.requests} requests and ${overview.errors} errors${effectiveConnectorId ? ` for ${connectorName(effectiveConnectorId).slice(0, 100)}` : " across connectors"}`;

  return (
    <MonitoringLayout
      ready
      headerItem={
        <MonitoringScopeSelector route={routes.monitoring.connectors} />
      }
    >
      <Content>
        <ConnectorsGrid>
          <ConnectorRequestVolumePanel
            action={
              <AskMonitoringChat
                disabled={isLoading || !usage}
                surface="connector-traffic"
                context={investigation}
                snapshot={trafficSnapshot}
              />
            }
            volume={volume}
            series={volumeSeries.series}
            seriesKeyOf={volumeSeries.seriesKeyOf}
            stats={stats}
            nowDay={today0}
            placeholder={isEmpty}
            filters={
              isEmpty ? (
                <>
                  {windowPicker}
                  {refreshButton}
                </>
              ) : (
                <>
                  {connectorPicker}
                  {spacePicker}
                  {windowPicker}
                  {refreshButton}
                </>
              )
            }
          />
          <ConnectorVolumeBars
            action={
              <AskMonitoringChat
                disabled={isLoading || !usage}
                surface="connector-requests"
                context={{
                  ...investigation,
                  filters: { ...investigation.filters, connectorId: null },
                }}
                snapshot={`${spaceSummaries.reduce((sum, row) => sum + row.requests, 0)} requests across ${spaceSummaries.length} connectors. All connectors in the selected space scope remain visible.`}
              />
            }
            bars={volumeBars}
            selectedConnectorId={effectiveConnectorId}
            placeholder={isEmpty}
            onToggle={(id) =>
              setParam(
                "connector",
                id === effectiveConnectorId ? null : id,
                "connectors.connector_filter_change"
              )
            }
            description={`Requests per connector ${selectedWindow.phrase}.`}
          />
          <ConnectorBreakdownPanel
            action={
              <AskMonitoringChat
                disabled={isLoading || !usage}
                surface="connector-errors"
                context={investigation}
                snapshot={trafficSnapshot}
              />
            }
            connectorSummaries={summaries}
            workflowSummaries={workflowSummaries}
            connectorMeta={connectorMeta}
            workflowsById={workflowsById}
            windowPhrase={selectedWindow.phrase}
            selectedConnectorId={effectiveConnectorId}
            unattributedSources={unattributedSources}
            placeholder={isEmpty}
            onClearConnector={() =>
              setParam("connector", null, "connectors.connector_filter_change")
            }
          />
        </ConnectorsGrid>
      </Content>
    </MonitoringLayout>
  );
}
